<template>
	<component
		:is="to ? AtomLink : 'button'"
		ref="buttonRef"
		v-bind="to"
		role="button"
		:data-testid="to ? 'AtomButton-link' : 'AtomButton-button'"
		:data-test-disabled="isDisabled ? true : undefined"
		:class="[
			'font-display font-medium',
			'rounded-full inline-flex items-center justify-center',
			'transition active:-translate-y-0 focus-visible:outline',
			'focus-visible:outline-offset-4 focus-visible:outline-2 focus-visible:-translate-y-1',
			{
				'border': ['xs', 'sm', 'md'].includes(size),
				'border-2': ['lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl'].includes(size),
			},
			{
				'bg-transparent border-transparent focus-visible:outline-brand-700':
					variant === ButtonVariant.Transparent,
				'bg-brand-500 border-brand-600 active:bg-brand-500 focus-visible:outline-brand-700':
					variant === ButtonVariant.Brand,
				'bg-red-400 border-red-500 active:bg-red-500 focus-visible:outline-red-700':
					variant === ButtonVariant.Red,
				'bg-green-500 border-green-600 active:bg-green-600 focus-visible:outline-green-600':
					variant === ButtonVariant.Green,
				'bg-transparent border-green-700 active:bg-green-400/10 focus-visible:outline-green-600':
					variant === ButtonVariant.GreenBorder,
				'bg-yellow-200 border-yellow-300 active:bg-yellow-300 focus-visible:outline-yellow-500':
					variant === ButtonVariant.Yellow,
				'bg-blue-400 border-blue-500 active:bg-blue-500 focus-visible:outline-blue-700':
					variant === ButtonVariant.Blue,
				'bg-gray-800 border-gray-900 active:bg-gray-900 focus-visible:outline-gray-900':
					variant === ButtonVariant.Dark,
				'bg-transparent border-gray-900 active:bg-gray-900/10 focus-visible:outline-gray-900':
					variant === ButtonVariant.DarkBorder,
				'bg-transparent border-white active:bg-gray-50/10 focus-visible:outline-gray-50':
					variant === ButtonVariant.LightBorder,
				'bg-gray-50 border-white active:bg-gray-100 focus-visible:outline-gray-50':
					variant === ButtonVariant.Light,
			},
			{
				'hover:cursor-not-allowed saturate-0 shadow-none hover:shadow-none':
					isDisabled,
				'hover:-translate-y-1': !isDisabled,
				'pointer-events-none': to && isDisabled,
			},
			{
				'py-2 gap-2 text-functional-size-xs': size === 'xs',
				'px-3.5 shadow hover:shadow-md':
					size === 'xs' && variant !== ButtonVariant.Transparent,
				'py-3.5 gap-3 text-functional-size-sm': size === 'sm',
				'px-4 shadow hover:shadow-md':
					size === 'sm' && variant !== ButtonVariant.Transparent,
				'py-3.5 gap-3.5 text-functional-size-md': size === 'md',
				'px-6 shadow-md hover:shadow-lg':
					size === 'md' && variant !== ButtonVariant.Transparent,
				'py-3.5 gap-4 text-functional-size-lg': size === 'lg',
				'px-7 shadow-lg hover:shadow-xl':
					size === 'lg' && variant !== ButtonVariant.Transparent,
				'py-3.5 gap-4 text-functional-size-lg lg:py-4 lg:gap-[1.3rem] lg:text-functional-size-xl':
					size === 'xl',
				'px-7 lg:px-10 shadow-lg lg:shadow-xl hover:shadow-xl lg:hover:shadow-2xl':
					size === 'xl' && variant !== ButtonVariant.Transparent,
			},
		]"
		@click.stop="
			(e: MouseEvent) => {
				if (isDisabled) {
					e.preventDefault();
					emit('disabledClick', e);
				}
				else {
					emit('click', e);
				}
			}
		"
	>
		<LazyAtomFontAwesome
			v-if="iconLeft"
			class="icon-left scale-125"
			:class="iconClasses"
			:icon="iconLeft"
		/>
		<span
			class="grow text-center"
			:class="textColor()"
		>{{ text }}</span>
		<LazyAtomFontAwesome
			v-if="iconRight"
			class="icon-right scale-125"
			:class="iconClasses"
			:icon="iconRight"
		/>
	</component>
</template>

<script lang="ts" setup>
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';
import type { ButtonType } from '#base/core/types/ButtonType';

const AtomLink = resolveComponent('AtomLink');

const buttonRef = ref<HTMLButtonElement | HTMLAnchorElement | null>(null);
defineExpose({
	buttonRef,
});

const {
	size = 'md',
	variant = ButtonVariant.Brand,
	isDisabled,
	iconLeft,
	iconRight,
	text,
	to,
} = defineProps<ButtonType>();

const emit = defineEmits(['click', 'disabledClick']);

const iconClasses = computed((): string[] => [
	textColor(),
	iconSizeClasses(size),
]);

const textColor = (): string => {
	switch (variant) {
		case ButtonVariant.Transparent:
			return isDisabled ? 'opacity-50' : '';
		case ButtonVariant.Brand:
			return isDisabled ? 'text-purple-50/70' : 'text-purple-50';
		case ButtonVariant.Red:
			return isDisabled ? 'text-red-50/70' : 'text-red-50';
		case ButtonVariant.Green:
			return isDisabled ? 'text-black/70' : 'text-black';
		case ButtonVariant.GreenBorder:
			return isDisabled ? 'text-green-600/70' : 'text-green-700';
		case ButtonVariant.Yellow:
			return isDisabled ? 'text-black/70' : 'text-black';
		case ButtonVariant.Blue:
			return isDisabled ? 'text-blue-50/70' : 'text-blue-50';
		case ButtonVariant.Dark:
			return isDisabled ? 'text-gray-50/70' : 'text-gray-50';
		case ButtonVariant.DarkBorder:
			return isDisabled ? 'text-gray-900/70' : 'text-gray-900';
		case ButtonVariant.LightBorder:
			return isDisabled ? 'text-gray-50/70' : 'text-gray-50';
		case ButtonVariant.Light:
			return isDisabled ? 'text-brand-900/70' : 'text-gray-900';
		default:
			return '';
	}
};

const iconSizeClasses = (size: string) => {
	switch (size) {
		case 'xs':
			return 'w-3 h-3';
		case 'sm':
			return 'w-4 h-4';
		case 'md':
			return 'w-5 h-5';
		case 'lg':
			return 'w-6 h-6';
		case 'xl':
			return 'w-7 h-7 lg:w-8 lg:h-8';
		default:
			return '';
	}
};
</script>
